import React, { useEffect, useRef, useCallback, useId, useState } from 'react';
import './Modal.css';

export interface ModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback when the modal should close
   */
  onClose: () => void;
  /**
   * Optional title displayed at the top of the modal
   */
  title?: string;
  /**
   * Modal content
   */
  children: React.ReactNode;
  /**
   * Optional footer content (typically action buttons)
   */
  footer?: React.ReactNode;
  /**
   * Size variant - small for confirmations, medium for forms, large for complex content
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether clicking the backdrop closes the modal
   * @default true
   */
  closeOnBackdropClick?: boolean;
  /**
   * Whether pressing Escape closes the modal
   * @default true
   */
  closeOnEscape?: boolean;
  /**
   * Whether to show the close button (X)
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Ref to the element that should receive focus when modal closes
   */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Optional id for the modal
   */
  id?: string;
  /**
   * Optional additional CSS classes
   */
  className?: string;
  /**
   * Callback fired after the modal opens and animations complete
   */
  onAfterOpen?: () => void;
  /**
   * Callback fired after the modal closes and animations complete
   */
  onAfterClose?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'medium',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  returnFocusRef,
  id,
  className = '',
  onAfterOpen,
  onAfterClose,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const generatedId = useId();
  const modalId = id || `hi-ui-modal-${generatedId}`;
  const titleId = `${modalId}-title`;

  // Handle render state - setState in effect is intentional for animation state machine
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      setShouldRender(true);
    } else if (shouldRender) {
      setIsClosing(true);
    }
  }, [isOpen, shouldRender]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Open the dialog when it mounts and isOpen is true
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && isOpen && !dialog.open) {
      dialog.showModal();
      // Focus the dialog container itself, not the first focusable element
      dialog.focus();
    }
  }, [isOpen, shouldRender]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('hi-ui-modal-open');
      return () => {
        document.body.classList.remove('hi-ui-modal-open');
      };
    }
  }, [isOpen]);

  // Handle native cancel event (Escape key)
  const handleCancel = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      if (closeOnEscape) {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Handle backdrop click (click on dialog element itself, not content)
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      if (closeOnBackdropClick && event.target === dialogRef.current) {
        onClose();
      }
    },
    [closeOnBackdropClick, onClose]
  );

  // Handle animation end
  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      dialogRef.current?.close();
      setIsClosing(false);
      setShouldRender(false);
      // Return focus
      const returnTo = returnFocusRef?.current || previousActiveElement.current;
      returnTo?.focus();
      onAfterClose?.();
    } else {
      // Opening animation completed
      onAfterOpen?.();
    }
  }, [isClosing, returnFocusRef, onAfterClose, onAfterOpen]);

  // Don't render until needed
  if (!shouldRender) {
    return null;
  }

  const modalClasses = [
    'hi-ui-modal',
    `hi-ui-modal--${size}`,
    isClosing ? 'hi-ui-modal--closing' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <dialog
      ref={dialogRef}
      className={modalClasses}
      id={modalId}
      tabIndex={-1}
      aria-labelledby={title ? titleId : undefined}
      onCancel={handleCancel}
      onClick={handleClick}
      onAnimationEnd={handleAnimationEnd}
      data-testid="modal"
    >
      {showCloseButton && (
        <button
          type="button"
          className="hi-ui-modal__close"
          onClick={onClose}
          aria-label="Close modal"
          data-testid="modal-close-button"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}

      {title && (
        <h2 className="hi-ui-modal__title" id={titleId}>
          {title}
        </h2>
      )}

      <div className="hi-ui-modal__body">{children}</div>

      {footer && <div className="hi-ui-modal__footer">{footer}</div>}
    </dialog>
  );
};
